import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './DeleteAccountPage.css';

const REASONS = [
  { value: 'no_longer_needed', label: 'I no longer need this account', icon: 'bi-person-dash' },
  { value: 'duplicate_account', label: 'I created another account', icon: 'bi-people' },
  { value: 'privacy_concerns', label: 'I have privacy concerns', icon: 'bi-shield-lock' },
  { value: 'too_many_notifications', label: 'I receive too many notifications', icon: 'bi-bell-slash' },
  { value: 'missing_features', label: 'The features I need are unavailable', icon: 'bi-grid' },
  { value: 'difficult_to_use', label: 'The application is difficult to use', icon: 'bi-compass' },
  { value: 'other', label: 'Other reason', icon: 'bi-three-dots' },
];

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [completed, setCompleted] = useState(false);

  const isStudent = String(user?.roleKey || '').trim().toLowerCase() === 'student';
  const canSubmit = useMemo(() => (
    isStudent
    && Boolean(reasonCode)
    && (reasonCode !== 'other' || reasonText.trim().length >= 5)
    && Boolean(password)
    && confirmed
    && !submitting
  ), [confirmed, isStudent, password, reasonCode, reasonText, submitting]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      await api.post('/account-deletion', {
        reasonCode,
        reasonText: reasonCode === 'other' ? reasonText.trim() : '',
        password,
        confirmed: true,
      });
      setCompleted(true);
      window.setTimeout(() => logout(), 1800);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Unable to delete your account. Please try again.');
      setSubmitting(false);
    }
  };

  if (!isStudent) {
    return (
      <main className="student-delete-account-page">
        <section className="student-delete-account-shell text-center">
          <span className="student-delete-account-icon neutral"><i className="bi bi-shield-lock" /></span>
          <h1>Account management</h1>
          <p>Self-service account deletion is available for student accounts. Please contact your administrator for assistance.</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/profile')}>Back to Profile</button>
        </section>
      </main>
    );
  }

  if (completed) {
    return (
      <main className="student-delete-account-page">
        <section className="student-delete-account-shell student-delete-account-success text-center" aria-live="polite">
          <span className="student-delete-account-icon success"><i className="bi bi-check-lg" /></span>
          <h1>Account deleted</h1>
          <p>Your access has been disabled successfully. You will now be signed out.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="student-delete-account-page">
      <div className="student-delete-account-heading">
        <button type="button" className="student-delete-account-back" onClick={() => navigate('/profile')} aria-label="Back to profile">
          <i className="bi bi-arrow-left" />
        </button>
        <div>
          <span>Account settings</span>
          <h1>Delete your account</h1>
          <p>This permanently removes access to your learning account.</p>
        </div>
      </div>

      <section className="student-delete-account-warning">
        <i className="bi bi-exclamation-triangle" />
        <div>
          <strong>Before you continue</strong>
          <p>You will be signed out and will no longer be able to access your courses, progress, certificates, saved items, or account benefits.</p>
        </div>
      </section>

      <form className="student-delete-account-shell" onSubmit={handleSubmit}>
        <div className="student-delete-account-section-heading">
          <span>1</span>
          <div>
            <h2>Why are you leaving?</h2>
            <p>Your feedback helps us improve the learning experience.</p>
          </div>
        </div>

        <div className="student-delete-reasons">
          {REASONS.map((reason) => (
            <label key={reason.value} className={reasonCode === reason.value ? 'selected' : ''}>
              <input
                type="radio"
                name="deletionReason"
                value={reason.value}
                checked={reasonCode === reason.value}
                onChange={(event) => setReasonCode(event.target.value)}
              />
              <i className={`bi ${reason.icon}`} />
              <span>{reason.label}</span>
              <i className="bi bi-check-circle-fill student-delete-reason-check" />
            </label>
          ))}
        </div>

        {reasonCode === 'other' && (
          <div className="student-delete-other-reason">
            <label htmlFor="account-delete-reason">Tell us your reason</label>
            <textarea
              id="account-delete-reason"
              rows="3"
              maxLength="500"
              value={reasonText}
              onChange={(event) => setReasonText(event.target.value)}
              placeholder="Please provide a short reason..."
              required
            />
            <small>{reasonText.length}/500</small>
          </div>
        )}

        <div className="student-delete-account-divider" />

        <div className="student-delete-account-section-heading">
          <span>2</span>
          <div>
            <h2>Confirm your identity</h2>
            <p>Enter your current password to protect your account.</p>
          </div>
        </div>

        <div className="student-delete-password">
          <label htmlFor="account-delete-password">Current password</label>
          <div>
            <i className="bi bi-lock" />
            <input
              id="account-delete-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Enter your password"
              required
            />
            <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
              <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
            </button>
          </div>
        </div>

        <label className="student-delete-confirmation">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          <span>I understand that deleting my account will immediately remove my access and sign me out.</span>
        </label>

        {errorMessage && <div className="student-delete-account-error" role="alert"><i className="bi bi-exclamation-circle" />{errorMessage}</div>}

        <div className="student-delete-account-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/profile')} disabled={submitting}>Keep My Account</button>
          <button type="submit" className="btn btn-danger" disabled={!canSubmit}>
            {submitting ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <i className="bi bi-trash3" />}
            Delete Account
          </button>
        </div>
      </form>
    </main>
  );
}
